import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import LoadingScreen from "@/components/LoadingScreen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ExternalLink,
  ArrowLeft,
} from "lucide-react";

interface UserReport {
  id: string;
  reported_user_id: string;
  reporter_id: string;
  category: string;
  description: string;
  linked_thread_ids: string[] | null;
  admin_response: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  reported_user?: {
    first_name: string | null;
    last_name: string | null;
  };
  reporter?: {
    first_name: string | null;
    last_name: string | null;
  };
}

interface LinkedRoom {
  id: string;
  name: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  harassment: "Harassment",
  inappropriate_content: "Inappropriate Content",
  fraud: "Fraud/Scam",
  off_platform_transaction: "Off-Platform Transaction",
  academic_dishonesty: "Academic Dishonesty",
  impersonation: "Impersonation",
  spam: "Spam",
  safety_concern: "Safety Concern",
  other: "Other",
};

const AdminUserReports = () => {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState<Record<string, string>>({});
  const [responding, setResponding] = useState<string | null>(null);
  const [linkedRooms, setLinkedRooms] = useState<Record<string, LinkedRoom[]>>({});

  useEffect(() => {
    if (isAdmin) {
      fetchReports();
    }
  }, [isAdmin]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const reportsWithProfiles: UserReport[] = [];
      for (const report of data || []) {
        const { data: reportedUser } = await supabase.rpc("get_public_profile", {
          profile_user_id: report.reported_user_id,
        });
        const { data: reporter } = await supabase.rpc("get_public_profile", {
          profile_user_id: report.reporter_id,
        });

        reportsWithProfiles.push({
          ...report,
          reported_user: reportedUser?.[0] || null,
          reporter: reporter?.[0] || null,
        });
      }

      setReports(reportsWithProfiles);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedRooms = async (reportId: string, threadIds: string[]) => {
    if (linkedRooms[reportId] || threadIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from("chat_rooms")
        .select("id, name")
        .in("id", threadIds);

      if (error) throw error;
      setLinkedRooms((prev) => ({ ...prev, [reportId]: data || [] }));
    } catch (error) {
      console.error("Error fetching linked rooms:", error);
    }
  };

  const handleExpand = (reportId: string, threadIds: string[] | null) => {
    if (expandedId === reportId) {
      setExpandedId(null);
    } else {
      setExpandedId(reportId);
      if (threadIds && threadIds.length > 0) {
        fetchLinkedRooms(reportId, threadIds);
      }
    }
  };

  const handleRespond = async (reportId: string) => {
    const response = responseText[reportId]?.trim();
    if (!response) {
      toast.error("Please enter a response");
      return;
    }

    setResponding(reportId);
    try {
      const { error } = await supabase
        .from("user_reports")
        .update({
          admin_response: response,
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
        })
        .eq("id", reportId);

      if (error) throw error;

      toast.success("Response submitted");
      setResponseText((prev) => ({ ...prev, [reportId]: "" }));
      fetchReports();
    } catch (error) {
      console.error("Error responding to report:", error);
      toast.error("Failed to submit response");
    } finally {
      setResponding(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getUserName = (profile: { first_name: string | null; last_name: string | null } | undefined) => {
    if (!profile) return "Unknown User";
    const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
    return name || "Unknown User";
  };

  if (roleLoading) {
    return <LoadingScreen />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const unresolvedCount = reports.filter((r) => !r.resolved_at).length;

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
          <h1 className="font-poppins font-bold text-4xl mb-4 text-foreground flex items-center gap-3">
            <AlertTriangle className="h-8 w-8" />
            User Reports
            {unresolvedCount > 0 && (
              <Badge variant="destructive" className="text-sm">
                {unresolvedCount} unresolved
              </Badge>
            )}
          </h1>
          <p className="font-inter text-xl text-muted-foreground">
            Review and respond to user reports
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-muted-foreground text-center">
                No reports submitted yet
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <Card key={report.id}>
                <Collapsible
                  open={expandedId === report.id}
                  onOpenChange={() => handleExpand(report.id, report.linked_thread_ids)}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge
                              variant={report.resolved_at ? "secondary" : "destructive"}
                            >
                              {report.resolved_at ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <AlertTriangle className="h-3 w-3 mr-1" />
                              )}
                              {report.resolved_at ? "Resolved" : "Pending"}
                            </Badge>
                            <Badge variant="outline">
                              {CATEGORY_LABELS[report.category] || report.category}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              Reported: {getUserName(report.reported_user)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(report.created_at)}
                            </span>
                          </div>
                        </div>
                        {expandedId === report.id ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="space-y-4 pt-0">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-muted-foreground mb-1">Reported User</p>
                          <Link
                            to={`/profile/${report.reported_user_id}`}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {getUserName(report.reported_user)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                        <div>
                          <p className="font-medium text-muted-foreground mb-1">Reported By</p>
                          <Link
                            to={`/profile/${report.reporter_id}`}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {getUserName(report.reporter)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>

                      <div>
                        <p className="font-medium text-muted-foreground text-sm mb-2">Description</p>
                        <p className="text-sm bg-muted p-3 rounded-lg border">
                          {report.description}
                        </p>
                      </div>

                      {report.linked_thread_ids && report.linked_thread_ids.length > 0 && (
                        <div>
                          <p className="font-medium text-muted-foreground text-sm mb-2 flex items-center gap-1">
                            <MessageSquare className="h-4 w-4" />
                            Linked Chat Rooms
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {linkedRooms[report.id]?.map((room) => (
                              <Link
                                key={room.id}
                                to={`/chat-rooms/${room.id}`}
                                className="text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
                              >
                                {room.name}
                              </Link>
                            )) || (
                              <span className="text-sm text-muted-foreground">
                                Loading rooms...
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {report.admin_response && (
                        <div>
                          <p className="font-medium text-muted-foreground text-sm mb-2">
                            Admin Response
                          </p>
                          <p className="text-sm bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-900">
                            {report.admin_response}
                          </p>
                          {report.resolved_at && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Resolved on {formatDate(report.resolved_at)}
                            </p>
                          )}
                        </div>
                      )}

                      {!report.resolved_at && (
                        <div className="pt-2 space-y-3">
                          <Textarea
                            placeholder="Enter your response to this report..."
                            value={responseText[report.id] || ""}
                            onChange={(e) =>
                              setResponseText((prev) => ({
                                ...prev,
                                [report.id]: e.target.value,
                              }))
                            }
                            className="min-h-[80px]"
                          />
                          <Button
                            onClick={() => handleRespond(report.id)}
                            disabled={responding === report.id}
                          >
                            {responding === report.id && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Submit Response & Resolve
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserReports;
